import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withKeys as _withKeys, normalizeClass as _normalizeClass, withDirectives as _withDirectives, TransitionGroup as _TransitionGroup, withModifiers as _withModifiers, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "column"
}
const _hoisted_2 = { class: "current-directory row" }
const _hoisted_3 = { class: "current-directory__name" }
const _hoisted_4 = {
  key: 0,
  class: "q-ml-lg row items-end"
}
const _hoisted_5 = { class: "row justify-between" }
const _hoisted_6 = { class: "row items-center" }
const _hoisted_7 = { class: "column items-end" }
const _hoisted_8 = {
  key: 0,
  class: "text-italic text-warning s-text-xs"
}
const _hoisted_9 = ["onClick", "onDblclick"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = {
  key: 4,
  class: "created-at"
}

import {
  convertUTCToLocal,
  toRussianLocaleDateTimeFromIso,
} from '@packages/toolbox/dist/utils/datetime';
import { cloneDeep, pick } from 'lodash';
import { InjectionKey, computed, nextTick, onMounted, ref } from 'vue';

import Uploader from 'SharedComponents/Attachments/Uploader.vue';
import DocumentsSkeleton from 'SharedComponents/Documents/DocumentsSkeleton.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import { useConfirmationDialog } from 'SharedDir/common/composables/useConfirmationDialog';
import useCustomLoading from 'SharedDir/common/composables/useCustomLoading';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { FsView, Modes, ServiceKey } from 'SharedDir/constants/file-system';
import { NestedLinkType } from 'SharedDir/constants/files';
import { AttachmentType, FileUploadOptions } from 'SharedDir/declarations/files';
import { FileSystemInterface, Folder } from 'SharedDir/declarations/filesystem';
import { removeRecordFromList, replaceRecordInList } from 'SharedDir/utils/helpers';
import { resolveService } from 'SharedDir/utils/vue';

const maxSize = 51200;
const maxFiles = 5;


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'Explorer' },
  __name: 'Explorer',
  props: {
    root: {},
    proMode: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {


const props = __props;
const currentDirectoryId = ref<number>(props.root);
const parents = ref<Record<number, null | number>>({ [props.root]: null });
const { loading, requestOptions } = useCustomLoading();
const { withConfirmation } = useConfirmationDialog();
const { failureNotification } = useNotifications();

const FS = resolveService(<InjectionKey<FileSystemInterface<A>>>ServiceKey, undefined, true);

const viewMode = ref<FsView>(FsView.table);
const directorySavingFailed = ref<boolean>(false);
const touched = ref<boolean>(false);
const available = ref<boolean>(false);
const currentDirectory = ref<A>(null as any);
const activeFolderId = ref<number | null>(null);
const editingFolder = ref<A | null>(null);
const editingMode = ref<Modes | null>(null);
const refList = ref(null as any);
const refDirectoryInput = ref<HTMLInputElement[]>(null as any);
const children = computed(() => {
  if (currentDirectory.value) {
    return currentDirectory.value.children;
  }
  return [];
});

// Максимально допустимый размер файла - 50 Мб.
function prettySize(size: number) {
  const power = Math.log10(size);
  if (power <= 3) {
    return `${size} Кб`;
  }

  const s = size / 1000;
  return `${Intl.NumberFormat('ru', { maximumFractionDigits: 1 }).format(s)} Мб`;
}

const uplodingRestrictionText = computed(
  () => `За один раз возможна загрузка не более ${maxFiles} файлов по ${prettySize(maxSize)}`
);

const tooltipUploadBtn = computed(
  () => `Нажмите для загрузки файлов. ${uplodingRestrictionText.value}`
);

const isReadonlyActiveFolder = computed(() => {
  const folder = children.value.find((item) => item.id === activeFolderId.value);
  if (!folder) {
    return false;
  }

  return folder.isReadonly;
});

function formatDate(v) {
  return toRussianLocaleDateTimeFromIso(convertUTCToLocal(v).toISOString());
}

async function addFolder() {
  if (editingMode.value) {
    return;
  }
  activeFolderId.value = null;
  editingMode.value = Modes.create;
  const parentId = currentDirectory.value.id < 0 ? null : currentDirectory.value.id;
  const newFolder = { id: -9999, children: [], documents: [], ...FS.newdir(parentId) };
  currentDirectory.value.children.push(newFolder);
  editingFolder.value = newFolder;
  await nextTick();
  refDirectoryInput.value[0].focus();
}

async function save(event: FocusEvent & { target: HTMLInputElement }) {
  if (!editingFolder.value || !editingFolder.value.name) {
    abort();
    return;
  }
  const folder = pick(editingFolder.value, ['name', 'parentId']);
  try {
    let request: () => ReturnType<
      FileSystemInterface<A>['mkdir'] | FileSystemInterface<A>['mvdir']
    >;
    if (editingMode.value === Modes.create) {
      request = () => FS.mkdir(folder);
    } else {
      request = () => FS.mvdir((editingFolder.value as Folder).id, folder);
    }
    const newFolder = await makeRequest(request, requestOptions);
    replaceRecordInList(currentDirectory.value.children, editingFolder.value.id, newFolder);
    finishEditing();
  } catch (e) {
    directorySavingFailed.value = true;
    touchInput();
    event.target.focus();
  }
}

function abort() {
  if (!editingFolder.value) {
    return;
  }
  if (editingMode.value === Modes.create) {
    removeRecordFromList(currentDirectory.value.children, editingFolder.value.id);
  }
  finishEditing();
}

async function getDirectory(dirId: null | number) {
  return makeRequest(() => FS.ls(dirId), requestOptions);
}

async function cd(dirId: Folder['id'], throwBreadcrumbs: boolean = true) {
  if (editingMode.value) {
    return;
  }
  currentDirectory.value = await getDirectory(dirId);
  if (throwBreadcrumbs && typeof parents.value[dirId] === 'undefined') {
    parents.value[dirId] = currentDirectoryId.value;
  }
  currentDirectoryId.value = dirId;
}

function setActive(folder: Folder) {
  if (editingMode.value) {
    return;
  }
  if (activeFolderId.value && activeFolderId.value === folder.id) {
    activeFolderId.value = null;
  } else {
    activeFolderId.value = folder.id;
  }
}

async function updateFolder() {
  const folder = currentDirectory.value.children.find(({ id }) => activeFolderId.value === id);
  if (!activeFolderId.value || !folder) {
    return;
  }
  editingMode.value = Modes.update;
  editingFolder.value = cloneDeep(folder);
  await nextTick();
  refDirectoryInput.value[0].focus();
}

async function removeFolder(event: KeyboardEvent) {
  if (!activeFolderId.value || event.code === 'Backspace') {
    return;
  }

  rm(activeFolderId.value);
}

function rm(folderId: number) {
  withConfirmation(
    {
      message: 'Вы действительно хотите удалить директорию? Действие является необратимым.',
      title: 'Подтверждение удаления',
    },
    async () => {
      try {
        await makeRequest(() => FS.rmdir(folderId), requestOptions);
        removeRecordFromList(currentDirectory.value.children, folderId);
        activeFolderId.value = null;
      } catch (e) {
        directorySavingFailed.value = true;
        touchInput();
        setTimeout(() => {
          directorySavingFailed.value = false;
        }, 1000);
      }
    }
  );
}

function moveActive(event: KeyboardEvent, direction: boolean) {
  const list = currentDirectory.value.children;
  const listLength = list.length;
  let activeIndex = list.findIndex(({ id }) => activeFolderId.value === id);
  if (!activeFolderId.value || activeIndex === -1) {
    activeIndex = 0;
    activeFolderId.value = list[activeIndex].id;
  }
  let newActiveIndex;
  if (direction) {
    newActiveIndex = activeIndex > 0 ? activeIndex - 1 : listLength - 1;
  } else {
    newActiveIndex = activeIndex < listLength - 1 ? activeIndex + 1 : 0;
  }
  activeFolderId.value = list[newActiveIndex].id;
}

function cdToActive() {
  if (!activeFolderId.value) {
    return;
  }
  cd(activeFolderId.value);
}

function goToParent() {
  const parentId = parents.value[currentDirectoryId.value];
  if (parentId) {
    cd(parentId, false);
  }
}

async function attachToCurrentDir({
  files,
  errors,
}: {
  files: Array<AttachmentType>;
  errors: Array<string>;
}) {
  if (errors.length > 0) {
    errors.forEach((message) => failureNotification(message));
  }
  currentDirectory.value.documents = [...currentDirectory.value.documents, ...files];
}

async function refresh(): Promise<void> {
  currentDirectory.value = await getDirectory(currentDirectoryId.value);
}

__expose({ refresh });

onMounted(async () => {
  await refresh();
  available.value = true;
});

function finishEditing() {
  editingFolder.value = null;
  editingMode.value = null;
  directorySavingFailed.value = false;
  activeFolderId.value = null;
}

function touchInput(duration = 200) {
  touched.value = true;
  setTimeout(() => {
    touched.value = false;
  }, duration);
}

// async function removeDocument(id: number) {
//   withConfirmation({}, async () => {
//     await makeRequest(() => FS.rmdoc(id), requestOptions);
//     removeRecordFromList(currentDirectory.value.documents, id);
//   });
// }

function focus() {
  refList.value.$el.focus();
}

function blur() {
  refList.value.$el.blur();
}

function uploadFile(file: FileList, o: FileUploadOptions) {
  const options = { ...o, folderId: currentDirectoryId.value };
  console.debug(options);
  return FS.uploadFile(file, options);
}

return (_ctx: any,_cache: any) => {
  const _component_s_btn = _resolveComponent("s-btn")!
  const _component_q_tooltip = _resolveComponent("q-tooltip")!

  return (currentDirectory.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "current-directory__label" }, "Текущая папка: ", -1)),
          _createElementVNode("span", _hoisted_3, _toDisplayString(currentDirectory.value.name), 1),
          (_unref(loading))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[6] || (_cache[6] = [
                _createElementVNode("span", { class: "text-bold q-mr-xs" }, "Идёт загрузка", -1),
                _createElementVNode("div", { class: "explorer-loader q-mb-xs" }, null, -1)
              ])))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_Transition, {
              class: "row items-center",
              tag: "div",
              name: viewMode.value === _unref(FsView).tile ? 'counter-clockwise' : 'clockwise',
              mode: "out-in"
            }, {
              default: _withCtx(() => [
                (viewMode.value === _unref(FsView).tile)
                  ? (_openBlock(), _createBlock(SIcon, {
                      key: "1",
                      name: "app:view-card",
                      color: "brand",
                      title: "Отображать файлы строками",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (viewMode.value = _unref(FsView).table))
                    }))
                  : (_openBlock(), _createBlock(SIcon, {
                      key: "2",
                      name: "app:view-table",
                      color: "brand",
                      title: "Отображать файлы карточками",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (viewMode.value = _unref(FsView).tile))
                    }))
              ]),
              _: 1
            }, 8, ["name"]),
            (!_ctx.readonly)
              ? (_openBlock(), _createBlock(_component_s_btn, {
                  key: 0,
                  class: "base-margin-left",
                  "no-caps": "",
                  unelevated: "",
                  type: "icon",
                  icon: "app:folder-add",
                  "text-color": "brand",
                  title: "Создать папку",
                  disable: currentDirectory.value.isReadonly,
                  onClick: addFolder
                }, null, 8, ["disable"]))
              : _createCommentVNode("", true),
            (!_ctx.readonly)
              ? (_openBlock(), _createBlock(_component_s_btn, {
                  key: 1,
                  class: "base-margin-left",
                  "no-caps": "",
                  unelevated: "",
                  type: "icon",
                  icon: "app:edit",
                  "text-color": "brand",
                  disable: !activeFolderId.value || currentDirectory.value.isReadonly || isReadonlyActiveFolder.value,
                  title: "Переименовать папку",
                  onClick: updateFolder
                }, null, 8, ["disable"]))
              : _createCommentVNode("", true),
            (!_ctx.readonly)
              ? (_openBlock(), _createBlock(_component_s_btn, {
                  key: 2,
                  class: "base-margin-left",
                  "no-caps": "",
                  unelevated: "",
                  type: "icon",
                  icon: "app:delete-2",
                  "text-color": "brand-red",
                  title: "Удалить папку",
                  disable: !activeFolderId.value || currentDirectory.value.isReadonly || isReadonlyActiveFolder.value,
                  onClick: removeFolder
                }, null, 8, ["disable"]))
              : _createCommentVNode("", true)
          ]),
          (!_ctx.readonly)
            ? (_openBlock(), _createBlock(Uploader, {
                key: 0,
                "ask-before-upload": _ctx.proMode,
                uploader: uploadFile,
                "max-size": maxSize,
                "max-files": maxFiles,
                "base-uploading-options": { linksToNestedFolders: _unref(NestedLinkType).none },
                onFinish: attachToCurrentDir
              }, {
                default: _withCtx(({ trigger }) => [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_s_btn, {
                      "no-caps": "",
                      type: "icon",
                      disable: editingMode.value !== null || currentDirectory.value.isReadonly,
                      icon: "app:import2",
                      title: "Загрузить документы",
                      onClick: trigger
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_tooltip, { "content-class": "s-tooltip" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(tooltipUploadBtn.value), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["disable", "onClick"]),
                    (currentDirectory.value.isReadonly)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Папка только для чтения"))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              }, 8, ["ask-before-upload", "base-uploading-options"]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_TransitionGroup, {
          ref_key: "refList",
          ref: refList,
          name: "fade",
          tag: "ul",
          class: "directories",
          tabindex: "0",
          onKeydown: [
            _withKeys(removeFolder, ["delete"]),
            _withKeys(updateFolder, ["f2"]),
            _withKeys(addFolder, ["f7"]),
            _withKeys(goToParent, ["backspace"]),
            _withKeys(cdToActive, ["enter"]),
            _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event: any) => (moveActive($event, true)), ["prevent","stop"]), ["up"])),
            _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event: any) => (moveActive($event, false)), ["prevent","stop"]), ["down"]))
          ]
        }, {
          default: _withCtx(() => [
            (currentDirectoryId.value && parents.value[currentDirectoryId.value])
              ? (_openBlock(), _createElementBlock("li", {
                  key: "f--1",
                  class: "directory",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (activeFolderId.value = null)),
                  onDblclick: goToParent
                }, [
                  _createVNode(SIcon, {
                    size: "24px",
                    name: "app:arrow-normal-top"
                  }),
                  _cache[8] || (_cache[8] = _createTextVNode(" Назад "))
                ], 32))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(children.value, (folder, folderIndex) => {
              return (_openBlock(), _createElementBlock("li", {
                key: `f-${folderIndex}`,
                class: _normalizeClass(["directory", { active: activeFolderId.value === folder.id, 'old-household': folder.isOutdated }]),
                onClick: ($event: any) => (setActive(folder)),
                onDblclick: ($event: any) => (cd(folder.id))
              }, [
                (folder.isOutdated)
                  ? (_openBlock(), _createBlock(SIcon, {
                      key: 0,
                      name: "info",
                      color: "error",
                      title: "Устаревшая директория"
                    }))
                  : _createCommentVNode("", true),
                (folder.isReadonly)
                  ? (_openBlock(), _createBlock(SIcon, {
                      key: 1,
                      name: "lock",
                      title: "Только для чтения"
                    }))
                  : _createCommentVNode("", true),
                (activeFolderId.value === folder.id)
                  ? (_openBlock(), _createBlock(SIcon, {
                      key: 2,
                      name: "custom:files/folder",
                      color: "brand"
                    }))
                  : (_openBlock(), _createBlock(SIcon, {
                      key: 3,
                      name: "custom:files/folder"
                    })),
                _createElementVNode("div", {
                  class: _normalizeClass(["name", { editing: editingFolder.value && editingFolder.value.id === folder.id }])
                }, [
                  (editingFolder.value && folder.id === editingFolder.value.id)
                    ? _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 0,
                        ref_for: true,
                        ref_key: "refDirectoryInput",
                        ref: refDirectoryInput,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((editingFolder.value.name) = $event)),
                        class: _normalizeClass({ failed: directorySavingFailed.value, touched: touched.value }),
                        onBlur: save,
                        onKeyup: [
                          _withKeys(save, ["enter"]),
                          _withKeys(abort, ["esc"])
                        ]
                      }, null, 34)), [
                        [_vModelText, editingFolder.value.name]
                      ])
                    : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(folder.name), 1))
                ], 2),
                (!editingFolder.value || editingFolder.value.id !== folder.id)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(folder.createdAt ? formatDate(folder.createdAt) : '-'), 1))
                  : _createCommentVNode("", true)
              ], 42, _hoisted_9))
            }), 128))
          ]),
          _: 1
        }, 512),
        (currentDirectory.value)
          ? _renderSlot(_ctx.$slots, "documents", {
              key: 0,
              view: viewMode.value,
              directory: currentDirectory.value
            })
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createBlock(DocumentsSkeleton, { key: 1 }))
}
}

})